import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Badge from 'react-bootstrap/Badge';

const QuizzesManager = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [studyCircles, setStudyCircles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [quizzes, setQuizzes] = useState([]);
    const [selectedStudyCircle, setSelectedStudyCircle] = useState('');
    const [selectedFolder, setSelectedFolder] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchStudyCircles();
    }, []);

    useEffect(() => {
        const savedCircle = localStorage.getItem('quizzes_selected_study_circle');
        if (savedCircle) {
            setSelectedStudyCircle(savedCircle);
        }
    }, []);

    useEffect(() => {
        if (selectedStudyCircle) {
            fetchFolders(selectedStudyCircle);
            localStorage.setItem('quizzes_selected_study_circle', selectedStudyCircle);
        } else {
            setFolders([]);
            setSelectedFolder('');
            localStorage.removeItem('quizzes_selected_study_circle');
        }
    }, [selectedStudyCircle]);

    useEffect(() => {
        if (folders.length > 0) {
            const savedFolder = localStorage.getItem('quizzes_selected_folder');
            if (savedFolder && folders.some(f => String(f.id) === savedFolder)) {
                setSelectedFolder(savedFolder);
            }
        }
    }, [folders]);

    useEffect(() => {
        if (selectedFolder) {
            fetchQuizzesByFolder(selectedFolder);
            localStorage.setItem('quizzes_selected_folder', selectedFolder);
        } else {
            setQuizzes([]);
            localStorage.removeItem('quizzes_selected_folder');
        }
    }, [selectedFolder]);

    const fetchStudyCircles = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/api/study-circles');
            setStudyCircles(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch study circles');
        } finally {
            setLoading(false);
        }
    };

    const fetchFolders = async (studyCircleId) => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get(`/api/study-circles/${studyCircleId}/folders`);
            setFolders(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch folders');
        } finally {
            setLoading(false);
        }
    };

    const fetchQuizzesByFolder = async (folderId) => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get(`/api/quizzes/folder/${folderId}`);
            setQuizzes(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch quizzes');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteQuiz = async (quizId) => {
        if (!window.confirm('Are you sure you want to delete this quiz?')) {
            return;
        }
        setError('');
        setSuccess('');
        try {
            await api.delete(`/api/quizzes/${quizId}`);
            setQuizzes(quizzes.filter(quiz => quiz.id !== quizId));
            setSuccess('Quiz deleted successfully');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete quiz');
        }
    };

    return (
        <div>
            <h2 className="mb-4">Quizzes</h2>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <div className="d-flex flex-wrap gap-3 mb-4 align-items-end">
                <div style={{ minWidth: 220 }}>
                    <Form.Label>Study Circle</Form.Label>
                    <Form.Select
                        value={selectedStudyCircle}
                        onChange={e => {
                            setSelectedStudyCircle(e.target.value);
                            setSelectedFolder('');
                        }}
                    >
                        <option value="">Select Study Circle</option>
                        {studyCircles.map(circle => (
                            <option key={circle.id} value={circle.id}>{circle.name}</option>
                        ))}
                    </Form.Select>
                </div>
                <div style={{ minWidth: 220 }}>
                    <Form.Label>Folder</Form.Label>
                    <Form.Select
                        value={selectedFolder}
                        onChange={e => setSelectedFolder(e.target.value)}
                        disabled={!selectedStudyCircle || folders.length === 0}
                    >
                        <option value="">Select Folder</option>
                        {folders.map(folder => (
                            <option key={folder.id} value={folder.id}>{folder.name}</option>
                        ))}
                    </Form.Select>
                </div>
                {user.role === 'admin' && (
                    <button
                        className="btn btn-primary ms-auto"
                        disabled={!selectedStudyCircle || !selectedFolder}
                        onClick={() => navigate(`/quiz-editor/new/${selectedStudyCircle}/${selectedFolder}`)}
                    >
                        Create Quiz
                    </button>
                )}
            </div>

            {loading && <div className="text-center">Loading...</div>}

            {selectedStudyCircle && selectedFolder && !loading && (
                <div className="row">
                    {quizzes.map(quiz => (
                        <div key={quiz.id} className="col-md-4 mb-4">
                            <div className="card h-100">
                                <div className="card-body">
                                    <h5 className="card-title">{quiz.title}</h5>
                                    <p className="card-text" dangerouslySetInnerHTML={{ __html: quiz.description }} />
                                    <div className="d-flex justify-content-between align-items-center">
                                        {user.role === 'admin' && (
                                            <>
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => navigate(`/quiz-editor/${quiz.id}`)}
                                                >
                                                    Edit Quiz
                                                </button>
                                                <button
                                                    className="btn btn-danger"
                                                    onClick={() => handleDeleteQuiz(quiz.id)}
                                                >
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                        {user.role !== 'admin' && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => navigate(`/quiz/${quiz.id}/attempt`)}
                                            >
                                                Take Quiz
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default QuizzesManager; 